/**
 * Block-explorer bases, keyed by the slugs the API uses for networks.
 *
 * The networks table is the source of truth and the API hands its
 * `explorer_url` down as `networkUrl`. This is a fallback for when it comes
 * back empty — an unseeded row, a row left inactive, or a chain added without
 * an explorer recorded. Without it the link is simply absent and a merchant
 * has no way to check their own transaction on-chain.
 *
 * Testnets get their own entry rather than sharing the parent chain's: the
 * icons collapse Sepolia into Ethereum, but a Sepolia hash on etherscan.io is
 * a 404.
 *
 * Being a hardcoded table, this can go stale. Seeding `explorer_url` on the
 * network row is the real fix for any chain that lands here.
 */

const EXPLORERS = {
  // Ethereum
  ethereum: 'https://etherscan.io',
  'ethereum-mainnet': 'https://etherscan.io',
  'sepolia-ethereum': 'https://sepolia.etherscan.io',
  'ethereum-sepolia': 'https://sepolia.etherscan.io',
  sepolia: 'https://sepolia.etherscan.io',
  holesky: 'https://holesky.etherscan.io',
  'ethereum-holesky': 'https://holesky.etherscan.io',

  // BNB Chain
  bsc: 'https://bscscan.com',
  'bnb-chain': 'https://bscscan.com',
  'bsc-testnet': 'https://testnet.bscscan.com',

  // Polygon
  polygon: 'https://polygonscan.com',
  'polygon-pos': 'https://polygonscan.com',
  'polygon-amoy': 'https://amoy.polygonscan.com',
  amoy: 'https://amoy.polygonscan.com',

  // Arbitrum
  arbitrum: 'https://arbiscan.io',
  'arbitrum-one': 'https://arbiscan.io',
  'arbitrum-sepolia': 'https://sepolia.arbiscan.io',

  // Optimism
  optimism: 'https://optimistic.etherscan.io',
  'op-mainnet': 'https://optimistic.etherscan.io',
  'optimism-sepolia': 'https://sepolia-optimism.etherscan.io',

  // Base
  base: 'https://basescan.org',
  'base-sepolia': 'https://sepolia.basescan.org',

  // Avalanche
  avalanche: 'https://snowtrace.io',
  'avalanche-fuji': 'https://testnet.snowtrace.io',
  fuji: 'https://testnet.snowtrace.io',
};

/** Slugs arrive as "sepolia-ethereum", "Sepolia Ethereum" or "sepolia_ethereum". */
const normalise = (value) =>
  String(value ?? '')
    .trim()
    .toLowerCase()
    .replace(/[\s_]+/g, '-')
    .replace(/-+/g, '-');

/** The explorer base for a network slug, or null when none is known. */
export const explorerBaseForNetwork = (network) => {
  const key = normalise(network);
  if (!key) return null;
  if (EXPLORERS[key]) return EXPLORERS[key];
  // "sepolia-ethereum" and "ethereum-sepolia" are the same chain; try the
  // other order before giving up.
  const parts = key.split('-');
  if (parts.length === 2) {
    const flipped = `${parts[1]}-${parts[0]}`;
    if (EXPLORERS[flipped]) return EXPLORERS[flipped];
  }
  return null;
};

/**
 * A transaction's explorer link, or null when one cannot be built.
 *
 * Prefers the API's own `networkUrl`; falls back to the table above. Returns
 * null unless the base is absolute http(s) — concatenating a null base used to
 * produce "null/tx/0x...", a RELATIVE url that the browser resolved against
 * the dashboard and opened as this app's own 404 page.
 */
export const explorerTxUrl = (networkUrl, network, txHash) => {
  if (!txHash) return null;
  const raw = networkUrl || explorerBaseForNetwork(network);
  if (!raw) return null;
  const base = String(raw).trim().replace(/\/+$/, '');
  if (!/^https?:\/\//i.test(base)) return null;
  return `${base}/tx/${txHash}`;
};

export default explorerTxUrl;
